import { createClient, type SupabaseClient } from '@supabase/supabase-js';

/**
 * ONE 플랫폼 통합표준 1절.
 *
 * 접속 정보는 번들에 하드코딩하지 않고 public/config.js 에서 읽는다.
 * 재빌드 없이 배포처에서 교체할 수 있어야 하기 때문이다.
 *
 * ⚠️ 여기서 다른 Supabase 프로젝트를 쓰면 ONE 플랫폼과 로그인 세션이 공유되지 않는다.
 *    그 순간 이 시스템의 인증 설계 전체가 무의미해진다.
 */
function readConfig(): { url: string; anonKey: string } {
  const cfg = window.SUPABASE_CONFIG;
  if (!cfg?.url || !cfg?.anonKey) {
    throw new Error(
      'SUPABASE_CONFIG 가 없습니다. index.html 이 ./config.js 를 로드하는지 확인하세요.'
    );
  }
  return cfg;
}

let client: SupabaseClient | null = null;

export function sb(): SupabaseClient {
  if (!client) {
    const { url, anonKey } = readConfig();
    client = createClient(url, anonKey, {
      auth: {
        // ONE 쉘과 동일 오리진이 아니어도 localStorage 세션은 브라우저에 공유된다.
        persistSession: true,
        autoRefreshToken: true,
        // 이 시스템은 자체 로그인 화면이 없으므로 URL 콜백 처리도 하지 않는다.
        detectSessionInUrl: false,
      },
    });
  }
  return client;
}

/** ONE 쉘 URL 이 아직 설정되지 않았을 때 던지는 표식. */
export const SHELL_URL_MISSING = 'SHELL_URL_MISSING';

/**
 * ONE 쉘 로그인 화면으로 이동 (통합표준 2절).
 *
 * @returns 이동을 시작했으면 true, 쉘 URL 이 설정되지 않아 이동할 수 없으면 false.
 *          잘못된 주소로 조용히 보내는 것보다 화면에 설정 안내를 띄우는 편이 안전하다.
 */
export function goShellLogin(): boolean {
  const shell = (window.ONE_SHELL_URL ?? '').trim();
  if (!shell) return false;

  // iframe 안에서 실행 중일 수 있으므로 반드시 최상위 창을 이동시켜야 한다.
  // location.href 를 쓰면 iframe 자기 자신만 이동해 "iframe 안에 쉘이 통째로 뜨는" 버그가 생긴다.
  try {
    window.top!.location.href = shell;
  } catch {
    window.location.href = shell;
  }
  return true;
}

/** 로그아웃 후 쉘로 복귀. */
export async function doLogout(): Promise<void> {
  try {
    await sb().auth.signOut();
  } finally {
    goShellLogin();
  }
}
